import React, { useEffect, useState } from "react";
import { Edit3, Save, X, Users, Shield, Briefcase, RefreshCw, Crown, Award, Calculator, FileText, ShoppingCart } from "lucide-react";
import Swal from "sweetalert2";

// Mock API Base URL - replace with your actual config
import API_BASE_URL from "../config";

export default function ApproverPositionManagement() {
    const [approvers, setApprovers] = useState([]);
    const [userLevels, setUserLevels] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Fetch Approvers
            const approversRes = await fetch(`${API_BASE_URL}api/M_Approver_Position`);
            if (!approversRes.ok) throw new Error("Failed to fetch approvers");
            const approversData = await approversRes.json();
            
            // Ensure exactly 3 records
            const threeApprovers = approversData.slice(0, 3);
            setApprovers(threeApprovers);

            // Fetch User Levels
            const userLevelsRes = await fetch(`${API_BASE_URL}api/UserLevels`);
            if (!userLevelsRes.ok) throw new Error("Failed to fetch user levels");
            const userLevelsData = await userLevelsRes.json();
            
            const activeUserLevels = userLevelsData.filter(u => u.isActive === 1);
            setUserLevels(activeUserLevels);

        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Failed to fetch data: ${err.message}`,
                confirmButtonColor: "#d33",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (approver) => {
        setEditingId(approver.id);
        setEditData({
            approverUserLevelId: approver.approverUserLevelId,
            approverPosition: approver.approverPosition,
            colum: approver.colum,
            pr: approver.pr ?? null,  // null if not set
            po: approver.po ?? null   // null if not set
        });
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditData({});
    };

    const handleSave = async (id) => {
        const result = await Swal.fire({
            title: 'Save Changes?',
            text: `Update ${editData.approverPosition}?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, save it!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            setSaving(true);

            const response = await fetch(`${API_BASE_URL}api/M_Approver_Position/${id}`, {
                method: 'PUT',
                headers: {
                    'accept': '*/*',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: id,
                    approverUserLevelId: editData.approverUserLevelId,
                    approverPosition: editData.approverPosition,
                    colum: editData.colum,
                    pr: editData.pr,
                    po: editData.po
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to save: ${errorText}`);
            }

            setEditingId(null);
            setEditData({});
            await fetchData();

            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Approver position updated successfully!",
                confirmButtonColor: "#3085d6",
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Failed to save: ${err.message}`,
                confirmButtonColor: "#d33",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id, position) => {
        const result = await Swal.fire({
            title: 'Delete Approver?',
            text: `Are you sure you want to delete ${position}? This action cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${API_BASE_URL}api/M_Approver_Position/${id}`, {
                method: 'DELETE',
                headers: {
                    'accept': '*/*',
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to delete: ${errorText}`);
            }

            await fetchData();

            Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: "Approver position has been deleted.",
                confirmButtonColor: "#3085d6",
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: `Failed to delete: ${err.message}`,
                confirmButtonColor: "#d33",
            });
        }
    };

    const getIconByColumn = (colum) => {
        switch(colum) {
            case 1: return Crown;
            case 2: return Award;
            case 3: return Calculator;
            default: return Briefcase;
        }
    };

    const getColorByColumn = (colum) => {
        switch(colum) {
            case 1: return "from-purple-400 to-purple-600";
            case 2: return "from-blue-400 to-blue-600";
            case 3: return "from-green-400 to-green-600";
            default: return "from-gray-400 to-gray-600";
        }
    };

    const getUserLevelName = (userLevelId) => {
        const userLevel = userLevels.find(u => u.id === userLevelId);
        return userLevel ? userLevel.userLevelName : "Unknown";
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-100 via-white to-blue-100">
                <div className="flex flex-col items-center gap-4 bg-white/40 backdrop-blur-xl border border-white/60 p-8 rounded-3xl shadow-2xl">
                    <div className="w-16 h-16 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
                    <p className="text-gray-700 text-lg font-bold">Loading approvers...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen via-white to-blue-100 p-6">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-8 bg-white/40 backdrop-blur-xl border border-white/60 rounded-3xl p-6 shadow-2xl">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800 mb-2">Approver Position Management</h1>
                            <p className="text-gray-600">Manage the 3 approval levels and their PR/PO permissions</p>
                        </div>
                        <button
                            onClick={fetchData}
                            className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                        >
                            <RefreshCw size={18} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* Cards Grid - Maximum 3 cards */}
                {approvers.length === 0 ? (
                    <div className="bg-white/40 backdrop-blur-xl border border-white/60 rounded-3xl p-12 shadow-2xl text-center">
                        <Shield className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <h3 className="text-xl font-bold text-gray-700 mb-2">No Approvers Found</h3>
                        <p className="text-gray-600">Please add approver positions to the system</p>
                    </div>
                ) : (
                    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-6">
                        {approvers.map((approver) => {
                            const isEditing = editingId === approver.id;
                            const IconComponent = getIconByColumn(approver.colum);

                            return (
                                <div
                                    key={approver.id}
                                    className={`relative rounded-3xl bg-white/40 backdrop-blur-2xl border border-white/60 shadow-2xl p-6 transition-all duration-500 hover:shadow-[0_20px_60px_-15px_rgba(147,51,234,0.5)] hover:scale-[1.03] hover:bg-white/50 ${
                                        isEditing ? "ring-2 ring-purple-400/60 shadow-[0_20px_60px_-15px_rgba(147,51,234,0.6)]" : ""
                                    }`}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-transparent to-blue-50/50 rounded-3xl pointer-events-none"></div>

                                    <div className="relative z-10">
                                        {/* Icon */}
                                        <div className={`w-16 h-16 bg-gradient-to-br ${getColorByColumn(approver.colum)} rounded-2xl flex items-center justify-center mb-4 shadow-xl`}>
                                            <IconComponent className="w-8 h-8 text-white" strokeWidth={2.5} />
                                        </div>

                                        {/* Title */}
                                        <div className="mb-4">
                                            <h3 className="text-xl font-bold text-gray-800 uppercase tracking-wide mb-1">
                                                {isEditing ? (
                                                    <input
                                                        type="text"
                                                        value={editData.approverPosition}
                                                        onChange={(e) => setEditData({...editData, approverPosition: e.target.value})}
                                                        className="w-full px-3 py-2 bg-white/60 border border-white/80 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                                                        placeholder="Position Name"
                                                    />
                                                ) : (
                                                    approver.approverPosition
                                                )}
                                            </h3>
                                            <p className="text-sm text-gray-600">Level {approver.colum} Approver</p>
                                        </div>

                                        <div className="border-t border-gray-300/50 mb-4"></div>

                                        {/* User Level Selection */}
                                        <div className="mb-4">
                                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
                                                Assigned User Level
                                            </label>
                                            {isEditing ? (
                                                <select
                                                    value={editData.approverUserLevelId}
                                                    onChange={(e) => setEditData({...editData, approverUserLevelId: parseInt(e.target.value)})}
                                                    className="w-full px-3 py-2 bg-white/60 border border-white/80 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                                                >
                                                    <option value="">Select User Level</option>
                                                    {userLevels.map(level => (
                                                        <option key={level.id} value={level.id}>
                                                            {level.userLevelName}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                                                    <Users className="w-4 h-4 text-purple-600" />
                                                    <span className="text-sm font-medium text-gray-800">
                                                        {getUserLevelName(approver.approverUserLevelId)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Column Number */}
                                        <div className="mb-4">
                                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 block">
                                                Approval Order
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="3"
                                                    value={editData.colum}
                                                    onChange={(e) => setEditData({...editData, colum: parseInt(e.target.value)})}
                                                    className="w-full px-3 py-2 bg-white/60 border border-white/80 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                                                />
                                            ) : (
                                                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg">
                                                    <span className="text-2xl font-bold text-purple-600">{approver.colum}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* PR/PO Permissions */}
                                        <div className="mb-4">
                                            <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3 block">
                                                Approval Permissions
                                            </label>
                                            
                                            {isEditing ? (
                                                <div className="space-y-2">
                                                    {/* PR Toggle */}
                                                    <div className="flex items-center justify-between p-3 bg-white/60 border border-white/80 rounded-lg">
                                                        <div className="flex items-center gap-2">
                                                            <FileText className="w-4 h-4 text-orange-600" />
                                                            <span className="text-sm font-medium text-gray-700">Purchase Request (PR)</span>
                                                        </div>
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={editData.pr === 1}
                                                                onChange={(e) => setEditData({
                                                                    ...editData, 
                                                                    pr: e.target.checked ? 1 : null
                                                                })}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                                                        </label>
                                                    </div>

                                                    {/* PO Toggle */}
                                                    <div className="flex items-center justify-between p-3 bg-white/60 border border-white/80 rounded-lg">
                                                        <div className="flex items-center gap-2">
                                                            <ShoppingCart className="w-4 h-4 text-blue-600" />
                                                            <span className="text-sm font-medium text-gray-700">Purchase Order (PO)</span>
                                                        </div>
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={editData.po === 1}
                                                                onChange={(e) => setEditData({
                                                                    ...editData, 
                                                                    po: e.target.checked ? 1 : null
                                                                })}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                                                        </label>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2">
                                                    {/* PR Badge */}
                                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-1 ${
                                                        approver.pr === 1 
                                                            ? 'bg-gradient-to-r from-orange-100 to-orange-200 border border-orange-300' 
                                                            : 'bg-gray-100 border border-gray-200 opacity-50'
                                                    }`}>
                                                        <FileText className={`w-3.5 h-3.5 ${approver.pr === 1 ? 'text-orange-600' : 'text-gray-400'}`} />
                                                        <span className={`text-xs font-semibold ${approver.pr === 1 ? 'text-orange-700' : 'text-gray-500'}`}>
                                                            PR
                                                        </span>
                                                    </div>

                                                    {/* PO Badge */}
                                                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-1 ${
                                                        approver.po === 1 
                                                            ? 'bg-gradient-to-r from-blue-100 to-blue-200 border border-blue-300' 
                                                            : 'bg-gray-100 border border-gray-200 opacity-50'
                                                    }`}>
                                                        <ShoppingCart className={`w-3.5 h-3.5 ${approver.po === 1 ? 'text-blue-600' : 'text-gray-400'}`} />
                                                        <span className={`text-xs font-semibold ${approver.po === 1 ? 'text-blue-700' : 'text-gray-500'}`}>
                                                            PO
                                                        </span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-2 mt-6">
                                            {isEditing ? (
                                                <>
                                                    <button
                                                        onClick={() => handleSave(approver.id)}
                                                        disabled={saving}
                                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50"
                                                    >
                                                        <Save size={16} />
                                                        <span className="text-sm font-medium">
                                                            {saving ? "Saving..." : "Save"}
                                                        </span>
                                                    </button>
                                                    <button
                                                        onClick={handleCancel}
                                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                                                    >
                                                        <X size={16} />
                                                        <span className="text-sm font-medium">Cancel</span>
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => handleEdit(approver)}
                                                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
                                                    >
                                                        <Edit3 size={16} />
                                                        <span className="text-sm font-medium">Edit</span>
                                                    </button>
                                                 
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    <div className="absolute inset-0 rounded-3xl opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-br from-white/20 via-transparent to-transparent"></div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Info Box */}
                <div className="mt-6 bg-blue-50/60 backdrop-blur-xl border border-blue-200/60 rounded-2xl p-4 shadow-lg">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div>
                            <h4 className="text-sm font-semibold text-blue-900 mb-1">Approval Flow & Permissions</h4>
                            <p className="text-xs text-blue-700">
                                The system supports exactly 3 approval levels. Each level must have a unique position name and order number (1, 2, or 3). 
                                Enable PR (Purchase Request) and/or PO (Purchase Order) permissions for each approver to control what documents they can approve.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}